import { useContent } from 'contexts/Content/Content';
import {
    getPlinkromaticCanonicalUrl,
    getPlinkromaticCanonicalUrlForPath,
    getPlinkromaticLessonHeaderSeo,
    getPlinkromaticRoutePath,
    getPlinkromaticSiteOrigin,
    getUrl,
    isPlinkromaticApprovedLessonHeaderPath,
    isPlinkromaticStaging,
} from 'helpers';
import Head from 'next/head';
import { useRouter } from 'next/router';

const INDEXABLE_ROBOTS_CONTENT = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

function toAbsoluteUrl(url: string, origin: string) {
    if (!url) {
        return url;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    try {
        return new URL(url, origin).toString();
    } catch {
        return url;
    }
}

function stripHtml(html: string) {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function truncateText(text: string, maxLength = 170) {
    if (text.length <= maxLength) {
        return text;
    }

    const shortened = text.slice(0, maxLength - 1).trimEnd();
    const boundary = shortened.lastIndexOf(' ');
    const safeText = boundary > 100 ? shortened.slice(0, boundary) : shortened;
    return `${safeText.trimEnd()}...`;
}

export default function DocHead() {
    const content = useContent();
    const router = useRouter();

    const {
        documentDescription,
        documentTitle,
        favicon,
        siteName,
        socialDescription,
        socialImage: defaultSocialImagePath,
        socialImageAlt: defaultSocialImageAlt,
        socialTitle,
    } = content.general;
    const routePath = getPlinkromaticRoutePath(router.asPath || router.pathname || '/');
    const canonicalUrl = getPlinkromaticCanonicalUrlForPath(routePath);
    const landingCanonicalUrl = getPlinkromaticCanonicalUrl();
    const siteOrigin = getPlinkromaticSiteOrigin();
    const faviconUrl = getUrl(favicon);
    const lessonId = routePath.match(/^\/lessons\/([^/]+)/)?.[1] || null;
    const lesson = lessonId ? content.lessons?.[lessonId] : null;
    const lessonTitle = lesson?.title ? stripHtml(content.parseHtml(lesson.title)) : '';
    const lessonDescription = lesson?.text ? truncateText(stripHtml(content.parseHtml(lesson.text))) : '';
    const isLandingRoute = routePath === '/';
    const isLessonHeaderRoute = isPlinkromaticApprovedLessonHeaderPath(routePath);
    const lessonHeaderSeo =
        lessonId && isLessonHeaderRoute
            ? getPlinkromaticLessonHeaderSeo(lessonId, {
                  lessonContent: lesson as IntroContent | null | undefined,
                  parseLessonHtml: content.parseHtml,
              })
            : null;
    const pageTitle =
        lessonHeaderSeo?.title ||
        (lessonId && lessonTitle ? `Lesson ${lessonId}: ${lessonTitle} | ${documentTitle}` : documentTitle);
    const pageDescription = lessonHeaderSeo?.description || lessonDescription || documentDescription;
    const shareTitle =
        lessonHeaderSeo?.title || (lessonId && lessonTitle ? `Lesson ${lessonId}: ${lessonTitle} | Plinkromatic` : socialTitle);
    const shareDescription = lessonHeaderSeo?.description || lessonDescription || socialDescription || documentDescription;
    const socialImagePath = lesson?.image || defaultSocialImagePath || favicon;
    const socialImageAlt = lesson?.imageAlt || defaultSocialImageAlt || 'Plinkromatic preview image';
    const socialImage = toAbsoluteUrl(getUrl(socialImagePath), siteOrigin);
    const shouldIndexRoute = (isLandingRoute || isLessonHeaderRoute) && !isPlinkromaticStaging();
    const robotsContent = shouldIndexRoute ? INDEXABLE_ROBOTS_CONTENT : 'noindex,follow';
    const featureList = [
        content.landing.point1?.label,
        content.landing.point2?.label,
        content.landing.point3?.label,
        content.landing.point4?.label,
        content.landing.point5?.label,
        content.landing.point6?.label,
    ].filter(Boolean);
    const webApplicationStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        applicationCategory: 'BusinessApplication',
        description: documentDescription,
        featureList,
        isAccessibleForFree: true,
        name: 'Plinkromatic',
        offers: {
            '@type': 'Offer',
            price: 0,
            priceCurrency: 'USD',
        },
        operatingSystem: 'Any',
        publisher: {
            '@type': 'Organization',
            name: siteName,
            url: siteOrigin,
        },
        url: landingCanonicalUrl,
    };

    return (
        <Head>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            <meta name="robots" content={robotsContent} />
            <meta name="googlebot" content={robotsContent} />
            <link rel="canonical" href={canonicalUrl} />
            <link rel="shortcut icon" href={faviconUrl} />
            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:title" content={shareTitle} />
            <meta property="og:description" content={shareDescription} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:image" content={socialImage} />
            <meta property="og:image:alt" content={socialImageAlt} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={shareTitle} />
            <meta name="twitter:description" content={shareDescription} />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:image" content={socialImage} />
            <meta name="twitter:image:alt" content={socialImageAlt} />
            {isLandingRoute ? (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(webApplicationStructuredData) }}
                />
            ) : null}
            {isLessonHeaderRoute && lessonHeaderSeo ? (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(lessonHeaderSeo.faqStructuredData) }}
                />
            ) : null}
        </Head>
    );
}
